import React from 'react';

export default function CandidateDrawer({ candidate, isOpen, onClose, scoringConfig }) {
  if (!isOpen || !candidate) return null;

  const getFitScoreTextColor = (score) => {
    const callNow = scoringConfig?.buckets?.call_now ?? 80;
    const warm = scoringConfig?.buckets?.warm ?? 60;
    if (score >= callNow) return 'text-emerald-700';
    if (score >= warm) return 'text-amber-700';
    return 'text-gray-700';
  };

  const getFitScoreBgColor = (score) => {
    const callNow = scoringConfig?.buckets?.call_now ?? 80;
    const warm = scoringConfig?.buckets?.warm ?? 60;
    if (score >= callNow) return 'bg-emerald-100';
    if (score >= warm) return 'bg-amber-100';
    return 'bg-gray-100';
  };

  const fitScore = candidate.fit_score || 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-[380px] bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900">
              {candidate.full_name || 'Unknown Candidate'}
            </h2>
            {candidate.local_name && (
              <p className="text-sm text-gray-500 mt-1">{candidate.local_name}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Fit Score Breakdown Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Fit Score Breakdown</h3>
              <span className="group relative inline-flex flex-shrink-0">
                <svg className="w-5 h-5 text-gray-400 hover:text-gray-600 cursor-help" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span className="absolute left-0 top-full mt-1 px-3 py-2 w-80 text-left text-sm font-normal bg-gray-900 text-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none">
                  The overall Fit Score is made from the five factors below. Each factor is scored 0–100% and combined with different weights to produce the final score. Use these to see why a candidate ranks higher or lower.
                </span>
              </span>
            </div>
            <div className="flex items-center justify-between mb-5">
              <span className="text-sm font-semibold text-gray-700">Overall Fit Score</span>
              <div className={`px-3 py-1 rounded-md ${getFitScoreBgColor(fitScore)}`}>
                <span className={`text-lg font-bold ${getFitScoreTextColor(fitScore)}`}>{fitScore}</span>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1 items-center gap-1">
                  <span className="text-sm text-gray-700 flex items-center gap-1">
                    Relationship Depth
                    <span className="group/item relative inline-flex flex-shrink-0">
                      <svg className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-help" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
                      <span className="absolute left-0 top-full mt-1 px-2 py-1.5 w-56 text-left text-xs font-normal bg-gray-900 text-white rounded shadow-lg opacity-0 invisible group-hover/item:opacity-100 group-hover/item:visible transition-all z-50 pointer-events-none">How strong your relationship is based on past interactions (calls, emails, placements). More recent and frequent contact increases this score.</span>
                    </span>
                  </span>
                  <span className="text-sm font-medium text-gray-900">{candidate.fit_score_breakdown?.relationship_depth || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-[#1B3A6B] h-2 rounded-full" style={{ width: `${candidate.fit_score_breakdown?.relationship_depth || 0}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1 items-center gap-1">
                  <span className="text-sm text-gray-700 flex items-center gap-1">
                    Contactability
                    <span className="group/item relative inline-flex flex-shrink-0">
                      <svg className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-help" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
                      <span className="absolute left-0 top-full mt-1 px-2 py-1.5 w-56 text-left text-xs font-normal bg-gray-900 text-white rounded shadow-lg opacity-0 invisible group-hover/item:opacity-100 group-hover/item:visible transition-all z-50 pointer-events-none">Whether we have a reliable way to reach them (e.g. email or phone). Higher when we have verified contact details.</span>
                    </span>
                  </span>
                  <span className="text-sm font-medium text-gray-900">{candidate.fit_score_breakdown?.contactability || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-[#00C6AE] h-2 rounded-full" style={{ width: `${candidate.fit_score_breakdown?.contactability || 0}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1 items-center gap-1">
                  <span className="text-sm text-gray-700 flex items-center gap-1">
                    Relevance
                    <span className="group/item relative inline-flex flex-shrink-0">
                      <svg className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-help" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
                      <span className="absolute left-0 top-full mt-1 px-2 py-1.5 w-56 text-left text-xs font-normal bg-gray-900 text-white rounded shadow-lg opacity-0 invisible group-hover/item:opacity-100 group-hover/item:visible transition-all z-50 pointer-events-none">How well their experience and role match your focus (sector, territory, role family). Derived from their profile and your targeting settings.</span>
                    </span>
                  </span>
                  <span className="text-sm font-medium text-gray-900">{candidate.fit_score_breakdown?.relevance || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-[#1B3A6B] h-2 rounded-full" style={{ width: `${candidate.fit_score_breakdown?.relevance || 0}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1 items-center gap-1">
                  <span className="text-sm text-gray-700 flex items-center gap-1">
                    Recency
                    <span className="group/item relative inline-flex flex-shrink-0">
                      <svg className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-help" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
                      <span className="absolute left-0 top-full mt-1 px-2 py-1.5 w-56 text-left text-xs font-normal bg-gray-900 text-white rounded shadow-lg opacity-0 invisible group-hover/item:opacity-100 group-hover/item:visible transition-all z-50 pointer-events-none">How recently you or your team had contact with them. More recent touchpoints score higher so you can prioritise warm leads.</span>
                    </span>
                  </span>
                  <span className="text-sm font-medium text-gray-900">{candidate.fit_score_breakdown?.recency || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-[#00C6AE] h-2 rounded-full" style={{ width: `${candidate.fit_score_breakdown?.recency || 0}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1 items-center gap-1">
                  <span className="text-sm text-gray-700 flex items-center gap-1">
                    Where Now Certainty
                    <span className="group/item relative inline-flex flex-shrink-0">
                      <svg className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-help" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
                      <span className="absolute left-0 top-full mt-1 px-2 py-1.5 w-56 text-left text-xs font-normal bg-gray-900 text-white rounded shadow-lg opacity-0 invisible group-hover/item:opacity-100 group-hover/item:visible transition-all z-50 pointer-events-none">How confident we are about their current employer and role. Higher when the information comes from ATS or candidate-confirmed sources rather than inferred or old data.</span>
                    </span>
                  </span>
                  <span className="text-sm font-medium text-gray-900">{candidate.fit_score_breakdown?.where_now_certainty || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-[#1B3A6B] h-2 rounded-full" style={{ width: `${candidate.fit_score_breakdown?.where_now_certainty || 0}%` }} />
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Details</h3>
            <div className="space-y-3">
              {candidate.primary_email && (
                <div className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm text-gray-900">{candidate.primary_email}</span>
                </div>
              )}
              {candidate.primary_phone && (
                <div className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="text-sm text-gray-900">{candidate.primary_phone}</span>
                </div>
              )}
            </div>
          </div>

          {/* Placement Timeline Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Placement Timeline</h3>
            <div className="space-y-4">
              {candidate.placement_history && candidate.placement_history.length > 0 ? (
                candidate.placement_history.map((placement, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#1B3A6B] mt-2"></div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {placement.from_company ? `${placement.from_company} → ` : ''}{placement.to_company}
                      </div>
                      <div className="text-sm text-gray-600">{placement.title}</div>
                      <div className="text-xs text-gray-500">{placement.year}</div>
                      {placement.hiring_manager && (
                        <div className="text-xs text-gray-500 mt-1">
                          HM: {placement.hiring_manager.name} ({placement.hiring_manager.title})
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No placement history available</p>
              )}
            </div>
          </div>

          {/* Warm Path Intelligence Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Warm Path Intelligence</h3>
            {candidate.warm_path ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-700">{candidate.warm_path.description}</p>
                {candidate.warm_path.hiring_manager && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Hiring Manager:</span> {candidate.warm_path.hiring_manager.name} ({candidate.warm_path.hiring_manager.title})
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No warm path information available</p>
            )}
          </div>

          {/* Data Provenance Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Provenance &amp; Sources</h3>
            <div className="space-y-3">
              {candidate.data_sources && candidate.data_sources.length > 0 ? (
                candidate.data_sources.map((source, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {source.type === 'ats' && (
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                        </svg>
                      )}
                      {source.type === 'spreadsheet' && (
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                        </svg>
                      )}
                      {(source.type === 'linkedin' || source.type === 'public') && (
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                      {source.type !== 'ats' && source.type !== 'spreadsheet' && source.type !== 'linkedin' && source.type !== 'public' && (
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                      <span className="text-sm text-gray-700 capitalize">{source.type}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-[#1B3A6B] h-2 rounded-full"
                          style={{ width: `${source.confidence || 0}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-12 text-right">{source.confidence || 0}%</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No data source information available</p>
              )}
            </div>
          </div>

          {/* Historical Interactions */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Historical Interactions</h3>
            <div className="space-y-3">
              {candidate.interactions && candidate.interactions.length > 0 ? (
                candidate.interactions.map((interaction, index) => (
                  <div key={index} className="flex items-start space-x-3 pb-3 border-b border-gray-100 last:border-0">
                    <div className="flex-shrink-0">
                      {interaction.type === 'call' && (
                        <svg className="w-5 h-5 text-[#00C6AE]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      )}
                      {interaction.type === 'email' && (
                        <svg className="w-5 h-5 text-[#1B3A6B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      )}
                      {interaction.type === 'placement' && (
                        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                      {interaction.type !== 'call' && interaction.type !== 'email' && interaction.type !== 'placement' && (
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h8m-8 4h5m5 6H6a2 2 0 01-2-2V6a2 2 0 012-2h8.586A2 2 0 0116 4.586l4.414 4.414A2 2 0 0121 10v8a2 2 0 01-2 2z" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 capitalize">{interaction.type}</div>
                      {interaction.description && (
                        <div className="text-sm text-gray-600">{interaction.description}</div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">{interaction.timestamp}</div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No interaction history available</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
